import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Returns MUI sx styles for clamping text to a fixed number of lines.
 */
export function lineClamp(lines: number) {
  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  } as const;
}

/**
 * Returns the singular or plural form based on count.
 * Example: pluralize(1, 'card', 'cards') => '1 card'
 */
export function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Shared sx styles for page-level h5 headings.
 * Used in DeckHeader, StudySetupHeader, NotesHeader, BillingPage.
 */
export const pageHeadingSx = {
  fontWeight: 600,
  fontSize: { xs: '1.25rem', sm: '1.5rem' },
  lineHeight: 1.2,
} as const;

/**
 * Returns sx styles for the grey "secondary action" button pattern
 * used in page headers (e.g. "New Loop", "Add New Card").
 */
export function greyButtonSx(theme: Theme) {
  return {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
    },
  };
}

/**
 * sx styles for section label typography in nav menus (e.g. "Navigation", "Account").
 */
export const menuSectionLabelSx: SxProps = {
  display: 'block',
  px: 2,
  pt: 2,
  pb: 0.5,
  color: 'text.disabled',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontSize: '0.65rem',
  fontWeight: 600,
};

/**
 * Returns sx styles for a nav menu item, handling active and inactive states.
 */
export function navMenuItemSx(active: boolean): SxProps {
  return {
    mx: 1,
    borderRadius: 2,
    color: active ? 'primary.main' : 'text.primary',
    backgroundColor: active ? 'action.selected' : 'transparent',
    '&:hover': {
      backgroundColor: active ? 'action.selected' : 'action.hover',
    },
  };
}
